import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { bandStatus } from "./bandLoading";
import {
  BAND_META, BAND_TONE_CLASSES, ESCAPE_HATCH_POSITIVE_THRESHOLD,
} from "./constants";

// One country tile in the "Chances by country" grid. Handles:
//   * headline stats + histogram bar + top-3 uni preview
//   * click-to-expand → band-chip picker (drives per-country drill-down)
//   * per-country WUA+ escape-hatch when the applicant is strong
//     globally but weak (<30% Likely+Target) for THIS country
//
// State stays in the parent — this component receives the country row,
// derived flags (`isOpen`, `isLoadingCountry`, `bandsOpen`) and the
// couple of callbacks it needs to fire (`onExpand`, `onToggleBand`).
export default function CountryCard({
  c,
  summary,
  isOpen,
  isLoadingCountry,
  loadError,
  loadedResults,
  onRetry,
  bandsOpen,
  onExpand,
  onToggleBand,
  intendedSubject,
}) {
  // Escape-hatch trigger — deliberately gated on global strength so it
  // doesn't compete with the global panel below the grid ("strong here,
  // weak elsewhere" pattern only).
  const globalTotal = summary.total_matches || 0;
  const globalPositive = summary.countries.reduce(
    (n, cc) => n + (cc.likely || 0) + (cc.target || 0), 0,
  );
  const globalPositiveRatio = globalTotal > 0 ? globalPositive / globalTotal : 0;
  const countryPositive = (c.likely || 0) + (c.target || 0);
  const countryPositiveRatio = c.total > 0 ? countryPositive / c.total : 0;
  const showPerCountryEscape = (
    c.total > 0
    && globalPositiveRatio >= ESCAPE_HATCH_POSITIVE_THRESHOLD
    && countryPositiveRatio < ESCAPE_HATCH_POSITIVE_THRESHOLD
  );
  const perCountryHref = (() => {
    const qs = new URLSearchParams({
      intent: "engaged",
      source: "quick-check-per-country",
      country: c.country,
    });
    if (intendedSubject) qs.set("intended_subject", intendedSubject);
    return `/wua-plus?${qs.toString()}`;
  })();

  const status = bandStatus({
    country: c.country, loading: isLoadingCountry, error: loadError, loadedResults, bandsOpen,
  });
  const bar = (n, tone) => n > 0 && (
    <div className={`h-1.5 rounded-full ${tone}`} style={{ flex: n }} />
  );

  return (
    <div
      className={`text-left border rounded-lg bg-white transition-colors ${
        isOpen ? "border-[#4C1D95]" : "border-stone-200"
      }`}
    >
      <button
        type="button"
        onClick={() => onExpand(c.country)}
        data-testid={`country-summary-${c.country}`}
        aria-expanded={isOpen}
        className={`w-full text-left p-4 rounded-lg transition-colors ${
          isOpen ? "" : "hover:border-stone-400"
        }`}
      >
        <div className="flex items-baseline justify-between">
          <div className="font-editorial text-lg text-stone-900">{c.country}</div>
          <div className="text-xs font-mono text-stone-500">{c.total}</div>
        </div>
        <div className="mt-3 flex gap-0.5 h-1.5">
          {bar(c.likely, "bg-emerald-500")}
          {bar(c.target, "bg-amber-400")}
          {bar(c.ambitious, "bg-orange-500")}
          {bar(c.highly_ambitious, "bg-red-500")}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500">
          {c.likely > 0 && <span><span className="text-emerald-700">{c.likely}</span> safety</span>}
          {c.target > 0 && <span><span className="text-amber-700">{c.target}</span> balanced</span>}
          {c.ambitious > 0 && <span><span className="text-orange-700">{c.ambitious}</span> stretch</span>}
          {c.highly_ambitious > 0 && <span><span className="text-red-700">{c.highly_ambitious}</span> long-shot</span>}
        </div>
        {c.top_universities?.length > 0 && (
          <div className="mt-2 text-[11px] text-stone-600 truncate">
            e.g. {c.top_universities.map((u) => u.name).slice(0, 3).join(" · ")}
          </div>
        )}
        <div className="mt-3 text-[11px] font-underground-sc tracking-wider text-[#4C1D95]">
          {isOpen ? "Hide bands ▲" : "Choose a band →"}
        </div>
      </button>

      {/* Band picker — only visible once a country is expanded. */}
      {isOpen && (
        <div
          data-testid={`country-bands-${c.country}`}
          className={`border-t border-stone-200 px-4 py-3 bg-[#FAF9F5] ${
            showPerCountryEscape ? "" : "rounded-b-lg"
          }`}
        >
          <div aria-busy={isLoadingCountry ? "true" : "false"} data-testid={`country-bands-region-${c.country}`}>
              <div className="label-uppercase !text-[10px] mb-2 text-[#4C1D95] flex items-center gap-1.5 font-semibold">
                <span className="inline-block w-4 h-[2px] bg-[#4C1D95]" />
                Click a band to reveal courses ↓
              </div>
              <div className="flex flex-wrap gap-1.5">
                {BAND_META.map((b) => {
                  const count = c[b.key] || 0;
                  if (count === 0) return null;
                  const isOn = !!bandsOpen[b.key];
                  const cls = BAND_TONE_CLASSES[b.tone];
                  return (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => onToggleBand(c.country, b.key)}
                      data-testid={`band-chip-${c.country}-${b.key}`}
                      aria-pressed={isOn}
                      className={`group text-xs font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-all hover:scale-[1.03] hover:shadow-sm ${
                        isOn ? cls.on : cls.off
                      }`}
                    >
                      {isOn && (status.pending.includes(b.key)
                        ? <Loader2 className="inline h-3 w-3 mr-1 align-[-2px] animate-spin motion-reduce:animate-none" aria-hidden="true" data-testid={`band-chip-spinner-${c.country}-${b.key}`} />
                        : <span className="mr-1">✓</span>)}
                      {b.label} <span className="font-semibold ml-1">{count}</span>
                      {!isOn && (
                        <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          →
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div role="status" aria-live="polite" data-testid={`country-band-status-${c.country}`}
                className={`text-[13px] mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 ${status.message ? "" : "sr-only"} ${loadError ? "text-rose-700" : "text-stone-700"}`}>
                {isLoadingCountry && <Loader2 data-testid={`country-band-spinner-${c.country}`} className="h-[18px] w-[18px] shrink-0 text-[#4C1D95] animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                <span>{status.message}</span>
              </div>
              {loadError && (
                <button type="button" onClick={() => onRetry(c.country)} data-testid={`country-band-retry-${c.country}`}
                  className="mt-1 min-h-[44px] text-xs font-semibold text-[#4C1D95] underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] rounded">
                  Try again
                </button>
              )}
          </div>
        </div>
      )}

      {/* Per-country WUA+ escape-hatch. Compact, sits inside the card
          so it doesn't compete with the fuller global panel below the
          grid. */}
      {showPerCountryEscape && (
        <div
          className="border-t border-[#4C1D95]/20 bg-[#4C1D95]/[0.04] px-4 py-3 rounded-b-lg"
          data-testid={`per-country-escape-${c.country}`}
        >
          <div className="flex items-start gap-2">
            {/* WUA+ crest — just the "+" glyph on a violet square instead of
                the full lockup. Text below already says "WUA+ application";
                the full logo was overwhelming the copy at this size.
                Rendered as inline SVG so it stays crisp at every zoom level
                with no extra network request. */}
            <span
              data-testid={`per-country-wua-badge-${c.country}`}
              aria-label="WUA+"
              className="wua-plus-pulse inline-flex items-center justify-center rounded-md bg-[#4C1D95] text-white shrink-0 mt-0.5 shadow-sm"
              style={{ width: 22, height: 22 }}
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                aria-hidden
              >
                <line x1="12" y1="4" x2="12" y2="20" />
                <line x1="4" y1="12" x2="20" y2="12" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-stone-800 leading-snug">
                Your profile is strong overall, but only{" "}
                <span className="font-semibold">
                  {Math.round(countryPositiveRatio * 100)}%
                </span>{" "}
                of your {c.country} matches are Likely or Target.
                Get 1:1 help sharpening your {c.country}{" "}
                application.
              </div>
              <div className="mt-2">
                <Link
                  to={perCountryHref}
                  data-testid={`per-country-wua-cta-${c.country}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-white bg-[#4C1D95] px-3 py-1.5 rounded-full hover:bg-[#3d1778] transition-colors"
                >
                  Get help!
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
